import { Injectable, inject } from '@angular/core';
import { AbstractControl, ValidatorFn, FormGroup } from '@angular/forms';
import { Observable, Subject, ReplaySubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { TimeService, HeaderSort, ArraySort } from '@shared';

import { TaskUser, SkillRequirement, PatientTaskView, TaskFeasibility, Patient, ProblemInstance } from '../../models';
import { ProblemInstanceService } from '../problem-instance/problem-instance.service';
import { FeasibilityService } from '../feasibility/feasibility.service';
import { MainResponsibleEmployeeView } from '../../models/main-responsible-employee-view.model';
import { RosterDepartment } from '../../models/roster-department.model';

@Injectable({
    providedIn: 'root'
})
export class PatientTaskService {
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly feasibilityService = inject(FeasibilityService);
    private readonly timeService = inject(TimeService);

    private taskFeasibility$: ReplaySubject<TaskFeasibility>;
    private readonly taskEditChanged$: Subject<Patient>;

    constructor() {
        this.taskEditChanged$ = new Subject();
        this.initPatientTasksFeasibility();
    }

    public getPatientTasks(patientId: string, sortValue?: HeaderSort): Observable<PatientTaskView[]> {
        return this.problemInstanceService.getProblemInstance().pipe(
            switchMap(result => {
                const skillLevels = result.problemInstance.skillLevels;
                const tasks = result.problemInstance.tasks.filter(task => task.patientId === patientId);

                if (sortValue) {
                    ArraySort.sort(
                        tasks,
                        sortValue.sortBy,
                        sortValue.sortAscending,
                        sortValue.secondarySortBy,
                        sortValue.secondarySortAscending
                    );
                }

                return this.getPatientTasksFeasibility().pipe(
                    map(feasibility =>
                        tasks.map(task => {
                            return this.mapTaskFromInstance(task, skillLevels, feasibility, result.problemInstance, result.problemInstance.rosterDepartments);
                        })
                    )
                );
            })
        );
    }

    public getHeavyTaskLowerLimit(): Observable<number> {
        return this.taskFeasibility$.pipe(map(result => result.feasibilitySuggestions.heavyTaskLowerLimit));
    }

    public getPatientTaskEditStateChange(): Observable<Patient> {
        return this.taskEditChanged$.asObservable();
    }

    public patientTaskEditStateChanged(patient?: Patient): void {
        this.taskEditChanged$.next(patient);
    }

    public taskDurationValidator(dateFrom: string, dateTo: string): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            if (!control.parent) {
                return {};
            }

            const fromValue = control.parent.controls[dateFrom].value;
            const toValue = control.parent.controls[dateTo].value;
            const duration = control.value;
            const timeDifference = this.timeService.timeDifference(fromValue, toValue);

            if (duration > timeDifference) {
                return {
                    durationTooLong: 'durationTooLong'
                };
            }

            return {};
        };
    }

    public mapTaskFromForm(taskForm: FormGroup, task: PatientTaskView): void {
        task.fromTime = taskForm.value.fromTime;
        task.toTime = taskForm.value.toTime;
        task.duration = taskForm.value.duration;

        if (task.skillRequirement !== taskForm.value.skillRequirement) {
            this.problemInstanceService.getProblemInstance().subscribe(result => {
                const skillLevel = result.problemInstance.skillLevels.find(
                    item => item.skillLevelId === taskForm.value.skillRequirement
                );
                task.skillRequirementDescription = skillLevel.skillLevelDescription;
            });
        }

        task.shiftType = taskForm.value.shiftType;
        task.skillRequirement = taskForm.value.skillRequirement;
        task.numberOfEmployees = taskForm.value.numberOfEmployees;
        task.permanent = taskForm.value.permanent;
        task.isActive = taskForm.value.isActive;
    }

    public updateProblemInstanceTask(taskView: PatientTaskView): void {
        this.problemInstanceService
            .getProblemInstance()
            .pipe(map(result => result.problemInstance.tasks.find(task => task.taskId === taskView.taskId)))
            .subscribe(task => {
                task.fromTime = this.timeService.convertTime(taskView.fromTime);
                task.toTime = this.timeService.convertTime(taskView.toTime);
                task.duration = taskView.duration;
                task.shiftType = taskView.shiftType;
                task.skillRequirement = taskView.skillRequirement;
                task.numberOfEmployees = taskView.numberOfEmployees;
                task.taskIsPermanent = taskView.permanent;
                task.isActive = taskView.isActive;
            });
    }

    public getPatientTasksFeasibility(): Observable<TaskFeasibility> {
        return this.taskFeasibility$.asObservable();
    }

    public initPatientTasksFeasibility(): void {
        this.taskFeasibility$ = new ReplaySubject(1);
        this.problemInstanceService
            .getProblemInstance()
            .pipe(
                switchMap(result => {
                    const taskIds = result.problemInstance.tasks.map(task => task.originalTaskId);
                    return this.feasibilityService
                        .getFeasibility(taskIds)
                        .pipe(map(feasibilityResult => feasibilityResult.taskFeasibility));
                })
            )
            .subscribe(this.taskFeasibility$);
    }

    public existPatientTaskWithHeavyTaskCapacityError(): Observable<boolean> {
        return this.getPatientTasksFeasibility().pipe(
            map(
                feasibility =>
                    feasibility.errors.heavyTaskCapacityErrors && feasibility.errors.heavyTaskCapacityErrors.length > 0
            )
        );
    }

    private mapTaskFromInstance(
        task: TaskUser,
        skillLevels: SkillRequirement[],
        feasibility: TaskFeasibility,
        problemInstance: ProblemInstance,
        rosterDepartments: RosterDepartment[]
    ): PatientTaskView {
        const skillLevel = skillLevels.find(item => item.skillLevelId === task.skillRequirement);
        const taskFeasibility = this.feasibilityService.getTaskFeasibilityInfo(task.originalTaskId, feasibility);
        return <PatientTaskView>{
            taskId: task.taskId,
            originalTaskId: task.originalTaskId,
            patientId: task.patientId,
            taskDescription: task.taskDescription,
            fromDate: task.fromDate,
            fromTime: this.timeService.convertTime(task.fromTime, true),
            toDate: task.toDate,
            toTime: this.timeService.convertTime(task.toTime, true),
            duration: task.duration,
            skillRequirement: task.skillRequirement,
            skillRequirementDescription: skillLevel ? skillLevel.skillLevelDescription : '',
            numberOfEmployees: task.numberOfEmployees,
            permanent: task.taskIsPermanent,
            feasibilityInfo: taskFeasibility,
            isSyncedInitially: task.isSyncedInitially,
            shiftType: task.shiftType,
            isHeavy: task.isHeavy,
            doesRequirePhysicalAppearance: task.doesRequirePhysicalAppearance,
            mainResponsibleEmployees: this.mapMainResponsibleEmployeesFromProblemInstance(task, problemInstance),
            rosterDepartmentName: rosterDepartments != null && task.rosterDepartmentId != null ? rosterDepartments
            .find(x => x.rosterDepartmentId  === task.rosterDepartmentId).rosterDepartmentName : null,
            isActive: task.isActive
        };
    }

    private mapMainResponsibleEmployeesFromProblemInstance(
        task: TaskUser,
        problemInstance: ProblemInstance
    ): MainResponsibleEmployeeView[] {
        const mainResponsibleEmployees: MainResponsibleEmployeeView[] = [];
        const patient = problemInstance.patients.find(p => p.patientId === task.patientId);
        const patientMainResponsibles = problemInstance.patientMainResponsibles.find(
            p => p.patientId === task.patientId
        );
        const compatibility = problemInstance.taskWorkShiftCompatibility.find(
            t => t.routePlannerTaskId === task.originalTaskId
        );

        if (!patientMainResponsibles || !compatibility) {
            return [];
        }

        patientMainResponsibles.employeeIdsMainResponsibles.forEach(employeeId => {
            const employeeWorkShift = problemInstance.employeeWorkShifts.find(
                e => e.employeeId === employeeId && compatibility.compatibleWorkHourIds.includes(e.workHourId)
            );

            if (employeeWorkShift) {
                mainResponsibleEmployees.push({
                    initials: employeeWorkShift.initials,
                    primaryContact: patient.primaryContactId === employeeWorkShift.employeeId,
                    secondaryContact: patient.secondaryContactId === employeeWorkShift.employeeId
                });
            }
        });

        return mainResponsibleEmployees;
    }
}
